import { Route, Switch, Redirect, useRouteMatch, useLocation } from "react-router-dom";
import { createContext, useCallback, useState } from "react";
import api from "../../../Services/api";
import { SendEmail } from "./SendEmail";
import { ConfirmCpf } from "./ConfirmCpf";
import { FaceLiveness } from "./FaceLiveness";
import { ValidateCode } from "./ValidateCode";
import { ChangePassword } from "./ChangePassword";
import { FIRST_ACCESS_SKIP_LIVENESS } from "./utils";
import type { FirstAccessContextValue, FirstAccessResult } from "./types";

export type FirstAccessLocationState = {
  username?: string;
  otpAlreadySent?: boolean;
  fromPasswordReset?: boolean;
};

export const MyFirstAccessContext = createContext<FirstAccessContextValue>(
  {} as FirstAccessContextValue
);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { description?: string; information?: string } } })
      .response?.data?.description === "string"
  ) {
    const data = (error as { response: { data: { description?: string; information?: string } } })
      .response.data;
    return data.information || data.description;
  }

  return undefined;
};

export const MyFirstAccess = () => {
  const { path } = useRouteMatch();
  const location = useLocation<FirstAccessLocationState | undefined>();
  const bootstrap = location.state;

  const [username, setUsername] = useState(bootstrap?.username ?? "");
  const [confirmedCpf, setConfirmedCpf] = useState(() => {
    if (!bootstrap?.fromPasswordReset || !bootstrap?.username) return "";
    const raw = String(bootstrap.username);
    if (raw.includes("@")) return "";
    return raw.replace(/\D/g, "");
  });
  const [livenessSessionId, setLivenessSessionId] = useState("");
  const [otpAlreadySent, setOtpAlreadySent] = useState(
    Boolean(bootstrap?.otpAlreadySent)
  );
  const [fromPasswordReset] = useState(Boolean(bootstrap?.fromPasswordReset));

  const identifyUser = useCallback(
    async (nextUsername = ""): Promise<FirstAccessResult> => {
      try {
        await api.post("/v1/auth/first-access/identify", {
          username: nextUsername,
        });
        setUsername(nextUsername);
        return { ok: true };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const confirmCpf = useCallback(
    async (nextUsername = "", cpf = ""): Promise<FirstAccessResult> => {
      try {
        const { data } = await api.post("/v1/auth/first-access/confirm-cpf", {
          username: nextUsername,
          cpf,
        });
        setUsername(nextUsername);
        setConfirmedCpf(cpf.replace(/\D/g, ""));
        return { ok: true, data: data || {} };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const startLiveness = useCallback(
    async (nextUsername = ""): Promise<FirstAccessResult> => {
      try {
        const { data } = await api.post("/v1/auth/first-access/liveness/start", {
          username: nextUsername,
        });
        const sessionId =
          data?.session_id || data?.sessionId || "";
        setLivenessSessionId(sessionId);
        return { ok: true, data: data || {} };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const completeLiveness = useCallback(
    async (nextUsername = "", sessionId = ""): Promise<FirstAccessResult> => {
      try {
        await api.post("/v1/auth/first-access/liveness/complete", {
          username: nextUsername,
          session_id: sessionId,
        });
        return { ok: true };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const getLivenessStatus = useCallback(
    async (nextUsername = "", sessionId = ""): Promise<FirstAccessResult> => {
      try {
        const { data } = await api.get(
          `/v1/auth/first-access/liveness/${encodeURIComponent(sessionId)}`,
          { params: { username: nextUsername } }
        );
        return { ok: true, data: data || {} };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const sendVerificationCode = useCallback(
    async (nextUsername = ""): Promise<FirstAccessResult> => {
      try {
        const { data } = await api.post("/v1/auth/otp/challenge", {
          username: nextUsername,
          channel: "email",
          purpose: "first_access",
        });

        if (data?.otp_delivered === false) {
          return {
            ok: false,
            message:
              (typeof data?.otp_delivery_detail === "string" && data.otp_delivery_detail.trim())
                || "Não foi possível enviar o código por e-mail.",
          };
        }

        setUsername(nextUsername);
        setOtpAlreadySent(true);
        return { ok: true };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const validateVerificationCode = useCallback(
    async (nextUsername = "", code = ""): Promise<FirstAccessResult> => {
      try {
        await api.post("/v1/auth/otp/verify", {
          username: nextUsername,
          code,
          channel: "email",
          purpose: "first_access",
        });

        return { ok: true };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  const changePassword = useCallback(
    async (
      nextUsername = "",
      password = "",
      passwordTransaction = ""
    ): Promise<FirstAccessResult> => {
      try {
        await api.post("/v1/auth/first-access/set-password", {
          username: nextUsername,
          password,
          password_transaction: passwordTransaction,
          channel: "email",
        });

        return { ok: true };
      } catch (error) {
        return { ok: false, message: getApiErrorMessage(error) };
      }
    },
    []
  );

  return (
    <MyFirstAccessContext.Provider
      value={{
        username,
        confirmedCpf,
        livenessSessionId,
        otpAlreadySent,
        fromPasswordReset,
        identifyUser,
        confirmCpf,
        startLiveness,
        completeLiveness,
        getLivenessStatus,
        sendVerificationCode,
        validateVerificationCode,
        changePassword,
        consumeOtpAlreadySent: () => setOtpAlreadySent(false),
      }}
    >
      <Switch>
        <Route exact path={path}>
          <SendEmail />
        </Route>
        <Route path={`${path}/confirm-cpf`}>
          <ConfirmCpf />
        </Route>
        <Route path={`${path}/face`}>
          {FIRST_ACCESS_SKIP_LIVENESS ? (
            <Redirect to={`${path}/validate-code`} />
          ) : (
            <FaceLiveness />
          )}
        </Route>
        <Route path={`${path}/validate-code`}>
          <ValidateCode />
        </Route>
        <Route path={`${path}/change-password`}>
          <ChangePassword />
        </Route>
      </Switch>
    </MyFirstAccessContext.Provider>
  );
};
